import React from "react";
import { FiLock, FiThumbsUp, FiThumbsDown, FiTrash2, FiCornerUpLeft } from "react-icons/fi";
import { AvatarRing } from "../../../common/comms/AvatarRing";
import { StatusBadge } from "../../../common/comms/StatusBadge";
import { ReadReceipt } from "../../../common/comms/ReadReceipt";
import { MessageMedia } from "../../../common/comms/MessageMedia";
import { PlanShareCard } from "../../../common/comms/PlanShareCard";
import { EventCard } from "../../../common/comms/EventCard";
import {
  isOwnSender,
  participantId,
  senderId,
} from "../../../utils/commsParticipantId";
import {
  senderDisplayName,
  unresolvedLabel,
} from "../../../utils/commsSenderName";
import { normalizePlanShare } from "../../../utils/commsPlanShare";
import { normalizeEventCard } from "../../../utils/commsEventCard";
import {
  buildMentionTargets,
  mentionsViewer,
  tokenizeMentions,
} from "../../../utils/commsMentions";
import {
  describeAttachments,
  normalizeAttachments,
} from "../../../utils/commsAttachments";

/** Own-bubble background per accent color. Full class strings (Tailwind-safe). */
const OWN_ACCENT = {
  coral: "bg-light-orange text-gray-900",
  blue: "bg-info-bg text-gray-900",
};

/**
 * Format a timestamp as a short local time (e.g. "3:42 PM").
 * @param {string} iso
 * @returns {string}
 */
const formatTime = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (err) {
    return "";
  }
};

/**
 * True when a message body has been removed by a coach (soft retract).
 * @param {Object} msg
 */
const isRetracted = (msg) => Boolean(msg.retracted);

/**
 * True when the underlying client deleted their own message content.
 * @param {Object} msg
 */
const isClientDeleted = (msg) =>
  !msg.retracted && (msg.content === null || msg.deleted === true);

/**
 * Small quoted preview of the message a bubble replies to.
 */
const QuotedReply = ({ quoted }) => {
  if (!quoted) return null;
  const text =
    typeof quoted.content === "string" ? quoted.content.trim() : "";
  const snippet =
    text.slice(0, 120) ||
    describeAttachments(normalizeAttachments(quoted)) ||
    "Attachment";
  return (
    <div className="mb-1 border-l-2 border-gray-300 pl-2 text-[11px] text-gray-500">
      <span className="font-medium">
        {quoted._senderName || "Reply"}
      </span>
      <span className="block max-w-full truncate">{snippet}</span>
    </div>
  );
};

/**
 * The text + media body used across bubble variants, with retract/delete
 * substitutions. When `mentionTargets` resolve, `@Name` tokens are styled —
 * and the token naming the viewer gets a stronger treatment.
 */
const BubbleBody = ({ msg, mentionTargets = [], viewerUserId }) => {
  if (isRetracted(msg)) {
    return <span className="italic text-gray-400">[Message removed]</span>;
  }
  if (isClientDeleted(msg)) {
    return (
      <span className="italic text-gray-400">
        [Message deleted by client]
      </span>
    );
  }
  const viewer = viewerUserId != null ? String(viewerUserId) : null;
  return (
    <>
      {msg.content && (
        <span className="whitespace-pre-wrap break-words">
          {tokenizeMentions(msg.content, mentionTargets).map((token, i) =>
            token.type === "mention" ? (
              <span
                key={i}
                className={
                  viewer != null && token.userId === viewer
                    ? "rounded bg-orange-primary/15 px-1 font-semibold text-orange-primary"
                    : "font-semibold text-orange-primary"
                }
              >
                {token.text}
              </span>
            ) : (
              token.text
            )
          )}
        </span>
      )}
      <MessageMedia message={msg} />
    </>
  );
};

/**
 * A single chat message rendered with sender-specific "bubble grammar".
 *
 * @param {Object} props
 * @param {Object} props.message
 * @param {string} props.viewerUserId
 * @param {string} props.viewerRole
 * @param {boolean} props.readOnly
 * @param {Array<Object>} props.participants
 * @param {Array<Object>} props.reads
 * @param {Array<Object>} props.activeCoaches - Co-coaches used for pending receipts.
 * @param {Map<string, Object>} props.messagesById - Lookup for quote replies.
 * @param {'coral'|'blue'} props.accentColor
 * @param {boolean} props.isLastOwn - True for the viewer's most recent message.
 * @param {(userId: string, role: string) => string|undefined} [props.resolveName] -
 *   Falls back to the shared identity cache when a participant record has no name yet.
 * @param {string} [props.viewerName] - The viewer's own display name, used to
 *   attribute their own messages. See `useViewerName`.
 * @param {(msg: Object) => void} props.onReply
 * @param {(id: string) => void} props.onDelete
 * @param {(id: string, feedback: string) => void} props.onAiFeedback
 */
export function MessageBubble({
  message,
  viewerUserId,
  viewerRole = "COACH",
  readOnly = false,
  participants = [],
  reads = [],
  activeCoaches = [],
  messagesById,
  accentColor = "coral",
  isLastOwn = false,
  resolveName,
  viewerName,
  onReply,
  onDelete,
  onAiFeedback,
  superseded = false,
}) {
  const senderRole = message.sender_role;
  const isSystem = message.message_type === 2;
  const isInternal = message.coach_only === true;
  const isAI = message.ai_generated === true || senderRole === "AI_BOT";
  // `isOwnSender` matches on either id form: ms-agents can put a coach's
  // employee_id on `sender_id`, and `viewerUserId` (FlexBoard's empId) may
  // itself be either form. Comparing only the normalized id misses half the
  // time, which renders the viewer's own message as a stranger.
  const isOwn = isOwnSender(message, viewerUserId);
  const isCoach = senderRole === "COACH" && !isOwn;

  // Mention rendering: resolve mentioned ids to the names the composer
  // inserted, and flag messages that mention the viewer (retracted messages
  // lose their priority treatment along with their content).
  const mentionTargets = buildMentionTargets({ message, participants, resolveName });
  const mentionsMe = mentionsViewer(message, viewerUserId) && !isRetracted(message);

  const sender = participants.find((p) => participantId(p) === senderId(message));
  const senderName = senderDisplayName({
    message,
    participants,
    resolveName,
    isOwn,
    viewerName,
  });
  const colorIndex = sender?.coach_color_index ?? 0;

  const quoted =
    message.reply_to_id && messagesById
      ? messagesById.get(message.reply_to_id)
      : null;
  if (quoted) {
    quoted._senderName = senderDisplayName({
      message: quoted,
      participants,
      resolveName,
      isOwn: isOwnSender(quoted, viewerUserId),
      viewerName,
    });
  }

  // Touch devices never hover, so below `sm` the reply affordance stays visible
  // rather than hiding behind group-hover.
  const replyAffordance = () =>
    !readOnly && onReply ? (
      <button
        type="button"
        onClick={() => onReply(message)}
        title="Reply"
        aria-label="Reply to message"
        className="flex-shrink-0 text-gray-400 transition-opacity hover:text-gray-600 sm:opacity-0 sm:group-hover:opacity-100"
      >
        <FiCornerUpLeft size={14} />
      </button>
    ) : null;

  // --- Plan card (workout/diet plan update) ---------------------------------
  // MUST stay above every sender branch. These messages are authored as the
  // coach who made the change, so `isOwn` is true for exactly the person most
  // likely to be looking at the thread — a branch placed lower would never run
  // for them, and they'd see the bare sentence with no plan attached.
  const planShare = normalizePlanShare(message);
  if (planShare && !isRetracted(message)) {
    return (
      <div className="group flex items-start gap-2 my-2">
        <AvatarRing name={senderName} colorIndex={colorIndex} size="sm" />
        <div className="min-w-0 max-w-[85%] sm:max-w-[75%]">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-[11px] font-semibold text-gray-700">
              {senderName}
            </span>
            <span className="text-[11px] text-gray-400">
              {formatTime(message.created_at)}
            </span>
            {replyAffordance()}
          </div>
          <div className="rounded-2xl rounded-tl-sm border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 sm:px-4">
            {/* Server-composed, rank-aware and within the push-banner budget —
                rendered verbatim. Do not rebuild or pluralize it here. */}
            {message.content && (
              <span className="whitespace-pre-wrap break-words">
                {message.content}
              </span>
            )}
            <PlanShareCard message={message} />
          </div>
        </div>
      </div>
    );
  }

  // --- Event card (a call booked, moved, cancelled, about to start) ----------
  // MUST stay above the system pill. These arrive as `message_type: 2`, so the
  // pill branch below would otherwise claim them and show only the sentence —
  // which is also exactly the right fallback when a card does not normalise.
  const eventCard = normalizeEventCard(message);
  if (eventCard && !isRetracted(message)) {
    return (
      <div className="my-3 flex justify-center">
        <EventCard
          card={eventCard}
          createdAt={message.created_at}
          superseded={superseded}
        />
      </div>
    );
  }

  // --- System pill ----------------------------------------------------------
  if (isSystem) {
    return (
      <div className="flex justify-center my-2">
        <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-500 text-[11px]">
          {message.content || "System update"}
        </span>
      </div>
    );
  }

  // --- Internal note (amber inset) ------------------------------------------
  if (isInternal) {
    return (
      <div className="group my-2 flex sm:mx-4">
        <div
          data-testid={mentionsMe ? "mention-highlight" : undefined}
          className={`w-full min-w-0 rounded-xl border border-internal/30 bg-internal-bg px-3 py-3 sm:px-4 ${
            mentionsMe ? "ring-1 ring-orange-primary/40" : ""
          }`}
        >
          <div className="flex items-center gap-1.5 text-internal-text text-[11px] font-semibold mb-1">
            <FiLock aria-hidden="true" />
            <span>Internal · hidden from client</span>
            <span className="ml-auto flex items-center gap-2">
              {replyAffordance()}
              <span className="text-internal-text/60">
                {formatTime(message.created_at)}
              </span>
            </span>
          </div>
          <div className="text-sm text-internal-text">
            <BubbleBody msg={message} mentionTargets={mentionTargets} viewerUserId={viewerUserId} />
          </div>
        </div>
      </div>
    );
  }

  // --- AI message -----------------------------------------------------------
  if (isAI) {
    const trigger = message.content_attributes?.trigger;
    const showActions = viewerRole === "COACH" && !readOnly;
    return (
      <div className="group flex items-start gap-2 my-2">
        <AvatarRing isAI size="sm" />
        <div className="min-w-0 max-w-[85%] sm:max-w-[75%]">
          <div className="flex items-center gap-2 mb-1">
            <StatusBadge tone="ai" dot>
              Flex AI
            </StatusBadge>
            {trigger && (
              <span className="text-[11px] text-ai-text/70">{trigger}</span>
            )}
          </div>
          <div className="rounded-2xl rounded-tl-sm bg-ai-bg text-ai-text italic px-4 py-2 text-sm">
            {quoted && <QuotedReply quoted={quoted} />}
            <BubbleBody msg={message} mentionTargets={mentionTargets} viewerUserId={viewerUserId} />
          </div>
          <div className="flex items-center gap-3 mt-1 text-[11px] text-gray-400">
            <span>Visible to client · delete to retract</span>
            {showActions && !isRetracted(message) && (
              <span className="flex items-center gap-2 transition-opacity sm:opacity-0 sm:group-hover:opacity-100">
                <button
                  type="button"
                  onClick={() => onAiFeedback?.(message.id, "THUMBS_UP")}
                  aria-label="Good AI reply"
                  className="hover:text-success"
                >
                  <FiThumbsUp size={13} />
                </button>
                <button
                  type="button"
                  onClick={() => onAiFeedback?.(message.id, "THUMBS_DOWN")}
                  aria-label="Poor AI reply"
                  className="hover:text-danger"
                >
                  <FiThumbsDown size={13} />
                </button>
                <button
                  type="button"
                  onClick={() => onDelete?.(message.id)}
                  aria-label="Delete AI message"
                  className="hover:text-danger"
                >
                  <FiTrash2 size={13} />
                </button>
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }

  // --- Own message (right aligned) ------------------------------------------
  if (isOwn) {
    const ownClass = OWN_ACCENT[accentColor] || OWN_ACCENT.coral;
    const receipt = computeReceipt(
      message,
      reads,
      participants,
      activeCoaches,
      viewerUserId,
      isLastOwn,
      resolveName
    );
    const failed = message.status === "failed";
    return (
      <div className="group flex flex-col items-end my-2">
        <div className="flex max-w-[85%] items-center gap-2 sm:max-w-[75%]">
          {replyAffordance()}
          <div className={`min-w-0 rounded-2xl rounded-tr-sm px-3 py-2 text-sm sm:px-4 ${ownClass}`}>
            {quoted && <QuotedReply quoted={quoted} />}
            <BubbleBody msg={message} mentionTargets={mentionTargets} viewerUserId={viewerUserId} />
          </div>
        </div>
        <div className="mt-0.5 flex max-w-[85%] items-center justify-end gap-2 sm:max-w-[75%]">
          <span className="truncate text-[11px] font-semibold text-gray-600">
            {senderName}
          </span>
          <span className="text-[11px] text-gray-400">
            {message.status === "sending"
              ? "Sending…"
              : formatTime(message.created_at)}
          </span>
          {failed && (
            <span className="text-[11px] text-danger">Failed to send</span>
          )}
        </div>
        {!failed && (
          <ReadReceipt seenBy={receipt.seenBy} pending={receipt.pending} />
        )}
      </div>
    );
  }

  // --- Co-coach & client (left aligned) -------------------------------------
  return (
    <div className="group flex items-start gap-2 my-2">
      {isCoach ? (
        <AvatarRing name={senderName} colorIndex={colorIndex} size="sm" />
      ) : (
        <AvatarRing name={senderName} colorIndex={0} size="sm" />
      )}
      <div className="min-w-0 max-w-[85%] sm:max-w-[75%]">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-[11px] font-semibold text-gray-700">
            {senderName}
          </span>
          <span className="text-[11px] text-gray-400">
            {formatTime(message.created_at)}
          </span>
          {replyAffordance()}
        </div>
        <div
          data-testid={mentionsMe ? "mention-highlight" : undefined}
          className={`rounded-2xl rounded-tl-sm border border-gray-200 px-3 py-2 text-sm text-gray-900 sm:px-4 ${
            mentionsMe
              ? "bg-light-orange ring-1 ring-orange-primary/40"
              : "bg-white"
          }`}
        >
          {quoted && <QuotedReply quoted={quoted} />}
          <BubbleBody msg={message} mentionTargets={mentionTargets} viewerUserId={viewerUserId} />
        </div>
      </div>
    </div>
  );
}

/**
 * Compute a read receipt for one of the viewer's own messages.
 * seenBy: participants whose last_read_seq >= msg.seq (excluding self + AI).
 * pending: active co-coaches who have not yet read the viewer's latest message.
 *
 * @param {(userId: string, role: string) => string|undefined} [resolveName] -
 *   Shared identity-cache lookup, tried before falling back to a role label
 *   (`unresolvedLabel`) — a raw id must never reach the UI.
 * @returns {{ seenBy: string[], pending: string[] }}
 */
function computeReceipt(
  message,
  reads,
  participants,
  activeCoaches,
  viewerUserId,
  isLastOwn,
  resolveName
) {
  const seenBy = [];
  for (const r of reads) {
    if (r.user_id === viewerUserId) continue;
    const p = participants.find((x) => participantId(x) === r.user_id);
    if (p?.participant_role === "AI_BOT") continue;
    if ((r.last_read_seq || 0) >= message.seq) {
      seenBy.push(
        p?.name ||
          resolveName?.(r.user_id, p?.participant_role) ||
          unresolvedLabel(p?.participant_role)
      );
    }
  }

  const pending = [];
  if (isLastOwn) {
    for (const coach of activeCoaches) {
      const coachId = participantId(coach);
      if (coachId === viewerUserId) continue;
      const r = reads.find((x) => x.user_id === coachId);
      if (!r || (r.last_read_seq || 0) < message.seq) {
        pending.push(
          coach.name || resolveName?.(coachId, "COACH") || unresolvedLabel("COACH")
        );
      }
    }
  }

  return { seenBy, pending };
}
